import { useEffect, useState, useSyncExternalStore } from "react";
import { loadOnboardingStatus, shouldResumeOnboarding } from "@/services/onboardingService";
import {
  isOnboardingStatusFresh,
  readOnboardingStatusEntry,
  subscribeToOnboardingStatus,
  type OnboardingStatusEntry,
} from "@/services/onboardingStatusCache";

/**
 * What the app shell should do with the current route.
 *
 * - `allow`    — render the routed page.
 * - `redirect` — send the user to `/onboarding`.
 * - `resolving`— we have never resolved a status this session; render a
 *                skeleton of the page we're about to show.
 */
export type OnboardingGateDecision = "allow" | "redirect" | "resolving";

interface Options {
  token: string | null;
  /** Routes that render inside the shell but are part of onboarding itself. */
  exempt: boolean;
  pathname: string;
}

/**
 * The whole rule, as a pure function — no DOM, no network, no React.
 *
 * Exported because this is the part that decides whether someone reaches the
 * product, and it is worth pinning directly: the cohort matrix behind it
 * (deferred guests, historical free/standard accounts bootstrapped straight to
 * the billing step, paid-but-unfinished users who are only pulled back at `/`)
 * is not obvious from any single call site.
 */
export function decideOnboardingGate({
  token,
  exempt,
  pathname,
  entry,
  coldLoadFailed,
}: Options & {
  entry: OnboardingStatusEntry | null;
  coldLoadFailed: boolean;
}): OnboardingGateDecision {
  if (!token) return "allow";
  if (exempt) return "allow";
  if (entry == null) return coldLoadFailed ? "redirect" : "resolving";
  // Invited guests explicitly opted out of onboarding; they are never funnelled.
  if (entry.status.onboarding_deferred) return "allow";
  return shouldResumeOnboarding(entry.status, pathname) ? "redirect" : "allow";
}

/**
 * The app-shell onboarding gate (AITS-590).
 *
 * Previously this refetched `/onboarding/status/` on every navigation and
 * returned a full-screen "Checking access…" while it waited, so moving between
 * dashboard, Settings and Workbench blanked the app three times — a full
 * network round trip each, seconds of it against a cold Cloud Run instance.
 *
 * The decision is two separate things, and only one of them needed the network:
 *  - the *status*, which changes only when the user acts (and every one of those
 *    paths writes through the cache), and
 *  - the *rule*, `shouldResumeOnboarding`, which is path-dependent — AITS-586
 *    made `/` pull an unfinished user back into setup even when they otherwise
 *    have access — and is pure, synchronous local logic.
 *
 * So the status is fetched once and re-evaluated per route. Navigation costs
 * nothing and can no longer flash.
 *
 * This is UX routing, not enforcement: the backend independently answers
 * `402 billing_onboarding_required` on the operations that matter, and
 * workbench/sources gate on workspace membership. Nothing is protected by
 * blocking the render — it only decides where to send you.
 */
export function useOnboardingGate({ token, exempt, pathname }: Options): OnboardingGateDecision {
  const entry = useSyncExternalStore(
    subscribeToOnboardingStatus,
    readOnboardingStatusEntry,
    readOnboardingStatusEntry,
  );

  // Only a *first* resolution can fail closed. Once we hold a status, a failed
  // background revalidate leaves the known-good answer in place rather than
  // ejecting someone mid-session over one flaky request.
  const [coldLoadFailed, setColdLoadFailed] = useState(false);

  useEffect(() => {
    if (!token || exempt) return;
    // The only synchronous check on a navigation, and the reason this is no
    // longer a per-route request: while the cached status is fresh there is
    // nothing to do — no fetch, no state change, no render.
    if (isOnboardingStatusFresh(entry)) return;

    let cancelled = false;
    loadOnboardingStatus()
      .then(() => {
        if (!cancelled) setColdLoadFailed(false);
      })
      .catch(() => {
        // `entry` is this effect's snapshot; a null one means nothing was
        // cached when the request started, so there is no answer to fall back
        // on and the gate fails closed (as it did before).
        if (!cancelled && entry == null) setColdLoadFailed(true);
      });

    return () => {
      cancelled = true;
    };
    // `pathname` is a dep so a stale entry gets revalidated as the user moves
    // around — but only ever in the background: the decision below is computed
    // from the entry we already hold, so a revalidate can't return the shell to
    // `resolving` and flash a skeleton. `entry` re-arms it on any cache write
    // (a fetch landing, a PATCH advancing the status, a 402 invalidating it).
  }, [token, exempt, pathname, entry]);

  return decideOnboardingGate({ token, exempt, pathname, entry, coldLoadFailed });
}

export default useOnboardingGate;
